/**
 * CreateDependencyModal — follows the same modal pattern as CreateItemModal.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { X, GitBranch, Loader2 } from 'lucide-react';
import { SearchableSelect } from '../../../components/shared/SearchableSelect';
import { dependencyPairKey } from './planningEngineViewShared';

/** One selectable endpoint — a work unit or an initiative (priority). */
export interface DependencyEndpointOption {
  value: string;
  label: string;
}

interface CreateDependencyModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Endpoints to choose between. The modal is grain-agnostic: the Dependencies
   * page drives it with work units on one tab and initiatives on the other, so
   * both grains get the same create affordance instead of only work units.
   */
  options: DependencyEndpointOption[];
  /** Singular noun for the grain, e.g. 'work unit' or 'initiative'. */
  entityLabel: string;
  /**
   * Pairs that already exist at this grain, as `${fromId}->${toId}`. Used to
   * refuse an exact duplicate: sync materializes relationships from
   * Prioritization under their own ids, so without this check the same pair can
   * be stored twice (once synced, once manual) and renders as two identical
   * rows with no way to tell them apart.
   */
  existingPairs: Set<string>;
  onSave: (dep: {
    fromId: string;
    toId: string;
    dependencyType: 'Hard' | 'Soft';
    blocking: boolean;
  }) => Promise<void>;
}


const DEP_TYPES: { value: 'Hard' | 'Soft'; label: string; description: string }[] = [
  { value: 'Hard', label: 'Hard', description: 'Must be resolved before the dependent can proceed' },
  { value: 'Soft', label: 'Soft', description: 'Should be considered, but not strictly blocking' },
];

export function CreateDependencyModal({
  isOpen,
  onClose,
  options,
  entityLabel,
  existingPairs,
  onSave,
}: CreateDependencyModalProps) {
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [depType, setDepType] = useState<'Hard' | 'Soft'>('Hard');
  const [blocking, setBlocking] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const backdropRef = useRef<HTMLDivElement>(null);

  const isSelfReference = Boolean(fromId && toId && fromId === toId);
  const isDuplicate = useMemo(
    () => Boolean(fromId && toId && existingPairs.has(dependencyPairKey(fromId, toId))),
    [fromId, toId, existingPairs],
  );

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setFromId('');
      setToId('');
      setDepType('Hard');
      setBlocking(true);
      setError('');
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const canSave = Boolean(fromId) && Boolean(toId) && !isSelfReference && !isDuplicate;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      await onSave({ fromId, toId, dependencyType: depType, blocking });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-[var(--bg-raised)] shadow-2xl shadow-gray-300/40 border border-[var(--rule)]/80 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-7 h-7 bg-[var(--slate-dim)]">
              <GitBranch className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[var(--text-pri)]">Add dependency</h2>
              <p className="text-xs text-[var(--text-ter)] mt-0.5">Link two {entityLabel}s in sequence</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <div className="px-6 py-5 space-y-4">
          {/* Dependent */}
          <div>
            <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">
              Dependent {entityLabel} <span className="text-[var(--coral)]">*</span>
            </label>
            <p className="text-xs text-[var(--text-ter)] mb-2">The {entityLabel} that depends on something else</p>
            <SearchableSelect
              options={options}
              value={fromId}
              onChange={setFromId}
              placeholder={`Search ${entityLabel}s…`}
              className="w-full"
            />
          </div>

          {/* Predecessor */}
          <div>
            <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">
              Predecessor {entityLabel} <span className="text-[var(--coral)]">*</span>
            </label>
            <p className="text-xs text-[var(--text-ter)] mb-2">Must come before the dependent</p>
            <SearchableSelect
              options={options}
              value={toId}
              onChange={setToId}
              placeholder={`Search ${entityLabel}s…`}
              className="w-full"
            />
            {isSelfReference && (
              <p className="mt-1 text-xs text-[var(--coral)]">
                Dependent and predecessor must be different {entityLabel}s.
              </p>
            )}
            {isDuplicate && (
              <p className="mt-1 text-xs text-[var(--coral)]">
                This dependency already exists — it may have been synced from a relationship in
                Prioritization. Adding it again would create a duplicate row.
              </p>
            )}
          </div>

          {/* Type */}
          <div>
            <label className="block text-xs font-semibold text-[var(--text-sec)] mb-2">Strength</label>
            <div className="space-y-2">
              {DEP_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setDepType(t.value)}
                  className={`w-full text-left px-4 py-3  border transition-all ${
                    depType === t.value
                      ? 'border-[var(--slate)] bg-[var(--slate-dim)] ring-1 ring-indigo-300'
                      : 'border-[var(--rule)] hover:border-[var(--rule)] bg-[var(--bg-raised)]'
                  }`}
                >
                  <p className={`text-sm font-semibold ${depType === t.value ? 'text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>
                    {t.label}
                  </p>
                  <p className={`text-xs mt-0.5 ${depType === t.value ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
                    {t.description}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Blocking */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={blocking}
              onChange={(e) => setBlocking(e.target.checked)}
              className="h-4 w-4 rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-indigo-500"
            />
            <span className="text-sm text-[var(--text-sec)]">Blocking — prevents downstream scheduling until satisfied</span>
          </label>

          {error && <p className="text-xs text-[var(--coral)]">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[var(--rule)] bg-[var(--surface)]/60">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={!canSave || saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Add dependency
          </button>
        </div>
      </div>
    </div>
  );
}
